import React, { useState, useEffect } from 'react'
import { Routes, Route, useLocation, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { useTheme } from './hooks/useTheme'
import { useAuth } from './hooks/useAuth'
import Layout from './components/layout/Layout'
import Sidebar from './components/layout/Sidebar'
import MobileNav from './components/layout/MobileNav'

// Pages
import Dashboard from './pages/Dashboard'
import Providers from './pages/Providers'
import Nodes from './pages/Nodes'
import SyncLogs from './pages/SyncLogs'
import V2BoardPanels from './pages/V2BoardPanels'
import Settings from './pages/Settings'
import Login from './pages/auth/Login'
import Register from './pages/auth/Register'
import ForgotPassword from './pages/auth/ForgotPassword'
import NotFound from './pages/NotFound'

// Loading components
function LoadingScreen() {
  return (
    <div className="min-h-screen-mobile flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-gray-900 dark:to-gray-800">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-secondary-500 dark:text-secondary-400">Đang tải...</p>
      </div>
    </div>
  )
}

function App() {
  const location = useLocation()
  const { theme } = useTheme()
  const { isAuthenticated, isLoading } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  // Show loading while checking auth
  if (isLoading) {
    return <LoadingScreen />
  }

  // Public routes - no authentication required
  const publicRoutes = (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  )

  // Protected routes - require authentication
  const protectedRoutes = (
    <Layout>
      <div className="flex min-h-screen-mobile">
        {/* Desktop Sidebar */}
        <div className="hidden lg:block">
          <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        </div>

        {/* Overlay for mobile sidebar */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-30 bg-black/50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Mobile Sidebar */}
        <div className={`lg:hidden fixed inset-y-0 left-0 z-40 w-64 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300`}>
          <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        </div>

        {/* Main Content */}
        <main className="flex-1 overflow-x-hidden bg-background-light dark:bg-background-dark">
          {/* Content wrapper */}
          <div className="container-mobile py-4 md:py-6 lg:py-8">
            <Routes>
              <Route index element={<Dashboard />} />
              <Route path="/providers" element={<Providers />} />
              <Route path="/nodes" element={<Nodes />} />
              <Route path="/sync-logs" element={<SyncLogs />} />
              <Route path="/v2board-panels" element={<V2BoardPanels />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </div>

          {/* Mobile Navigation */}
          <MobileNav />
        </main>
      </div>
    </Layout>
  )

  return (
    <>
      {/* Toast Notifications */}
      <Toaster
        position="bottom-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: theme === 'dark' ? '#1e293b' : '#ffffff',
            color: theme === 'dark' ? '#ffffff' : '#1e293b',
            border: theme === 'dark' ? '1px solid #334155' : '1px solid #e2e8f0',
            borderRadius: '0.75rem',
          },
        }}
      />

      {/* Render routes based on auth state */}
      {isAuthenticated ? protectedRoutes : publicRoutes}
    </>
  )
}

export default App
